import { makeAutoObservable } from "mobx";
import type { CollectionsApi } from "@/api/collections";
import { browserStorage, type CacheStorage, readListCache, writeListCache } from "./listCache";
import { PagedResource, Resource } from "./Resource";

/** The API pages episodes 30 at a time */
const EPISODES_PAGE_SIZE = 30;

const LIST_CACHE_KEY = "hp_collections_v2";
const LIST_CACHE_MAX_AGE = 1000 * 60 * 60 * 24; // 24 hours

/**
 * The poddsamlingar: the list of collections, and one collection with its episodes.
 *
 * Like CategoryStore, server data lives in resources keyed by the collection slug.
 */
export class CollectionStore {
    readonly list: Resource<Collection[]>;
    readonly detail: Resource<Collection, string>;
    readonly feed: PagedResource<Episode, string>;

    constructor(
        private readonly api: CollectionsApi,
        private readonly storage: CacheStorage | null = browserStorage()
    ) {
        this.list = new Resource(() => this.fetchList(), {
            initial: readListCache<Collection[]>(this.storage, LIST_CACHE_KEY, LIST_CACHE_MAX_AGE),
        });
        this.detail = new Resource((slug) => this.api.bySlug(slug));
        this.feed = new PagedResource(
            (slug, page) => this.idFor(slug).then((id) => this.api.episodes(id, page)),
            EPISODES_PAGE_SIZE
        );

        makeAutoObservable<CollectionStore, "api" | "storage">(this, {
            api: false,
            storage: false,
            list: false,
            detail: false,
            feed: false,
        });
    }

    /** The public collections */
    get collections(): Collection[] {
        return (this.list.data ?? []).filter((c) => !c.private);
    }

    get collectionsLoaded() {
        return this.list.loaded;
    }

    get collection(): Collection | null {
        return this.detail.data ?? null;
    }

    get episodes(): Episode[] {
        return this.feed.items;
    }

    /** True while another page of episodes is being fetched */
    get loading() {
        return this.feed.loading;
    }

    /** True once the collection and its first page of episodes have arrived for `slug` */
    isOpen(slug: string) {
        return [this.detail, this.feed].every((r) => r.isFor(slug) && r.loaded);
    }

    /**
     * Loads a collection with its first page of episodes. Settles when both have arrived, so a page that
     * suspends on it never shows half a collection, and rejects when the collection itself cannot be
     * loaded, so the page can show its error state.
     */
    async open(slug: string) {
        await Promise.all([this.detail.load(slug), this.feed.load(slug)]);
        if (this.detail.status === "error") {
            throw this.detail.error;
        }
    }

    /** Loads the list unless it is loaded or loading. Rejects when it cannot be loaded, so a page can show its error state. */
    async loadList() {
        await this.list.load();
        if (this.list.status === "error") {
            throw this.list.error;
        }
    }

    loadMore() {
        return this.feed.loadMore();
    }

    // The episodes are asked for by id, which only the collection itself knows
    private async idFor(slug: string): Promise<number> {
        await (this.detail.isFor(slug) ? this.detail.whenSettled() : this.detail.load(slug));
        if (!this.detail.isFor(slug) || !this.detail.data) {
            throw this.detail.error ?? new Error(`Collection not found: ${slug}`);
        }
        return this.detail.data.id;
    }

    private async fetchList(): Promise<Collection[]> {
        const collections = await this.api.list();
        writeListCache(this.storage, LIST_CACHE_KEY, collections);
        return collections;
    }
}
